// Lucka 3: Kvittensuppföljning per etapp – hur många entreprenörer har kvitterat AMP och ordningsregler.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import DocumentHeader from "./DocumentHeader";
import { ClipboardCheck, CheckCircle2, CircleDashed, FileDown } from "lucide-react";
import { generateAcknowledgementsWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Stage {
  id: string;
  name: string;
  status: string;
}

interface Contractor {
  id: string;
  company: string;
  contact_email: string | null;
  is_bas_u: boolean;
  stage_id: string;
}

interface Invite {
  id: string;
  contact_email: string | null;
  company: string | null;
  stage_id: string | null;
}

interface Ack {
  invite_id: string;
  document_type: string;
  acknowledged_at: string;
  acknowledged_by: string;
}

const TRACKED_DOCS = [
  { key: "amp", label: "Arbetsmiljöplan" },
  { key: "skyddsregler", label: "Skydds- och ordningsregler" },
];

const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

const AcknowledgementTracking: React.FC<Props> = ({ projectId }) => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [acks, setAcks] = useState<Ack[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [stageRes, contractorRes, inviteRes, ackRes] = await Promise.all([
      supabase.from("project_stages").select("id, name, status").eq("project_id", projectId).order("sort_order"),
      supabase
        .from("stage_contractors")
        .select("id, company, contact_email, is_bas_u, stage_id")
        .eq("project_id", projectId),
      supabase.from("contractor_portal_invites").select("id, contact_email, company, stage_id").eq("project_id", projectId),
      supabase
        .from("contractor_acknowledgements")
        .select("invite_id, document_type, acknowledged_at, acknowledged_by")
        .eq("project_id", projectId),
    ]);
    if (stageRes.error || contractorRes.error) toast.error("Kunde inte läsa kvittenserna");
    setStages((stageRes.data as Stage[] | null) ?? []);
    setContractors((contractorRes.data as Contractor[] | null) ?? []);
    setInvites((inviteRes.data as Invite[] | null) ?? []);
    setAcks((ackRes.data as Ack[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Kopplar entreprenör → portalinbjudan via e-post, annars företagsnamn.
  const inviteFor = useCallback(
    (c: Contractor) =>
      invites.find(
        (i) =>
          (norm(c.contact_email) && norm(i.contact_email) === norm(c.contact_email)) ||
          (norm(c.company) && norm(i.company) === norm(c.company))
      ) ?? null,
    [invites]
  );

  const rows = useMemo(
    () =>
      stages.map((stage) => {
        const list = contractors.filter((c) => c.stage_id === stage.id);
        const entries = list.map((c) => {
          const invite = inviteFor(c);
          const done = TRACKED_DOCS.map((d) => ({
            ...d,
            ack: invite
              ? acks.find((a) => a.invite_id === invite.id && a.document_type === d.key) ?? null
              : null,
          }));
          return { contractor: c, invite, done };
        });
        const totalChecks = entries.length * TRACKED_DOCS.length;
        const doneChecks = entries.reduce(
          (sum, e) => sum + e.done.filter((d) => d.ack).length,
          0
        );
        return {
          stage,
          entries,
          percent: totalChecks ? Math.round((doneChecks / totalChecks) * 100) : 0,
          doneChecks,
          totalChecks,
        };
      }),
    [stages, contractors, acks, inviteFor]
  );

  const overall = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.totalChecks, 0);
    const done = rows.reduce((s, r) => s + r.doneChecks, 0);
    return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
  }, [rows]);

  const exportWord = async () => {
    try {
      await generateAcknowledgementsWordDocument(
        projectId,
        rows.flatMap((r) =>
          r.entries.map((e) => ({
            company: e.contractor.company,
            email: e.contractor.contact_email ?? "–",
            stage: r.stage.name,
            documents: e.done.map((d) => ({ label: d.label, acknowledgedAt: d.ack?.acknowledged_at ?? null })),
          }))
        )
      );
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="15b"
        title="Kvittensuppföljning per etapp"
        description="Vilka entreprenörer har tagit del av arbetsmiljöplanen och ordningsreglerna – och vilka saknas fortfarande."
        icon={ClipboardCheck}
        projectId={projectId}
        exportHistory={{ documentType: "acknowledgements" }}
        actions={
          <Button size="sm" variant="outline" onClick={exportWord}>
            <FileDown className="mr-2 h-4 w-4" /> Exportera Dok 15b
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Alla etapper</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Progress value={overall.percent} />
          <p className="text-sm text-muted-foreground">
            {overall.done} av {overall.total} kvittenser inlämnade ({overall.percent} %).
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga etapper med entreprenörer ännu. Lägg till entreprenörer per etapp så följs kvittenserna här.
          </CardContent>
        </Card>
      ) : (
        rows.map((r) => (
          <Card key={r.stage.id}>
            <CardHeader className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base">{r.stage.name}</CardTitle>
                <Badge variant={r.percent === 100 ? "default" : "secondary"}>
                  {r.doneChecks}/{r.totalChecks} kvitterat
                </Badge>
              </div>
              <Progress value={r.percent} />
            </CardHeader>
            <CardContent className="space-y-2">
              {r.entries.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga entreprenörer kopplade till etappen.</p>
              ) : (
                r.entries.map((e) => (
                  <div key={e.contractor.id} className="rounded-md border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{e.contractor.company}</span>
                      {e.contractor.is_bas_u && <Badge variant="outline">Bas-U</Badge>}
                      {!e.invite && (
                        <Badge variant="secondary">Ingen portalinbjudan skickad</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4">
                      {e.done.map((d) => (
                        <span key={d.key} className="flex items-center gap-1.5 text-sm">
                          {d.ack ? (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          ) : (
                            <CircleDashed className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={d.ack ? "" : "text-muted-foreground"}>
                            {d.label}
                            {d.ack
                              ? ` · ${new Date(d.ack.acknowledged_at).toLocaleDateString("sv-SE")}`
                              : ""}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default AcknowledgementTracking;
